import {auditTime, combineLatest, map, Observable, startWith} from 'rxjs';
import type {DashboardTime} from '../../utils/chart-data-provider';

interface MeasurementQueryFeatures {
    includeWeather?: boolean;
    includePower?: boolean;
    includeClimateNormalization?: boolean;
}

interface DashboardSourceLike {
    type: unknown;
    id: string;
}

interface FingerprintedDashboardData {
    queryFingerprint?: string;
}

export interface DashboardTotalsContext extends MeasurementQueryFeatures {
    groupByEntityId?: boolean;
    groupByEntityIdPossible?: boolean;
    reportMode?: boolean;
    hasDashboardSelectedSources?: boolean;
    hasFilterSources?: boolean;
    meterDashboard?: boolean;
    portfolio?: boolean;
}

export function withMeasurementRefresh<T>(source: Observable<T>, refresh: Observable<void>): Observable<T> {
    return combineLatest([
        source,
        refresh.pipe(startWith(undefined), auditTime(0))
    ]).pipe(map(([value]) => value));
}

export function canReuseDashboardMeasurements(
    requestedRange: DashboardTime,
    dashboardRange: DashboardTime,
    features: MeasurementQueryFeatures
): boolean {
    return !features.includeWeather && !features.includeClimateNormalization
        && requestedRange?.start === dashboardRange?.start
        && requestedRange?.end === dashboardRange?.end
        && requestedRange?.resolution === dashboardRange?.resolution;
}

export function canUseDashboardTotals(context: DashboardTotalsContext): boolean {
    return !context.groupByEntityId && !context.groupByEntityIdPossible
        && !context.includeWeather && !context.includePower && !context.includeClimateNormalization
        && !context.reportMode && !context.hasDashboardSelectedSources && !context.hasFilterSources
        && !context.meterDashboard && context.portfolio === true;
}

export function dashboardQueryFingerprint(timeRange: DashboardTime, sources: DashboardSourceLike[]): string {
    return JSON.stringify({
        timeRange: timeRange ? {
            start: timeRange.start,
            end: timeRange.end,
            resolution: timeRange.resolution
        } : null,
        sources: (sources || []).map(source => `${source.type}:${source.id}`).sort()
    });
}

export function dashboardDataMatchesFilters(data: FingerprintedDashboardData, timeRange: DashboardTime,
                                            sources: DashboardSourceLike[]): boolean {
    return data?.queryFingerprint === dashboardQueryFingerprint(timeRange, sources);
}
